import { CollectionApiResponse } from 'api/api.types';
import { getApiToken } from 'api/apiRequest';
import * as CollectionApi from 'api/Collection';
import { GetServerSideProps, NextPage } from 'next';
import getConfig from 'next/config';
import { useEffect } from 'react';
import { Analytics, Events } from 'src/services/analytics';
import { getCollectionStatus } from 'utils/collection.utils';
import { slugify } from 'utils/global';
import { normalizeCollection } from 'utils/normalizers/collection';
import React from 'react';
import CollectionPage from 'src/page-components/collection/old/CollectionPage';
import useAuth from 'providers/hooks/useAuth';
import CollectionNSIPage from 'src/page-components/collection/CollectionNSIPage';
import HeadMetadata from 'src/components/metadata/HeadMetadata';
import { getCollectionPageMetadata } from 'utils/metadata.utils';

interface InitialProps {
  isInitialLoggedIn: boolean;
  collection: CollectionApiResponse;
}

const CollectionDynamicPage: NextPage<InitialProps> = ({
  collection,
  isInitialLoggedIn,
}) => {
  const normalizedCollection = normalizeCollection(collection);

  const { isLoggedIn } = useAuth();

  useEffect(() => {
    Analytics.logEvent({
      eventName: Events.consumption.viewCollection,
      properties: {
        collectionId: collection.id,
        collectionStatus: getCollectionStatus({
          collection: normalizedCollection,
        }),
      },
      platforms: ['amplitude'],
    });
  }, []);

  if (!isLoggedIn) {
    return (
      <>
        <HeadMetadata
          pageMetadata={getCollectionPageMetadata({
            collection: normalizedCollection,
          })}
        />
        <CollectionNSIPage collection={collection} />
      </>
    );
  }

  return (
    <CollectionPage
      collection={normalizedCollection}
      isInitialLoggedIn={isInitialLoggedIn}
    />
  );
};

export const getServerSideProps: GetServerSideProps<InitialProps> = async ({
  params,
  req,
}) => {
  if (!params) return { notFound: true };

  const token = getApiToken({ requestServer: req, allowDefault: true });

  const id = Number(params.id);

  const collection = await CollectionApi.getCollection({
    id,
    token,
    similarCount: 5,
  }).catch(() => undefined);

  if (!collection) return { notFound: true };
  const slug = slugify(collection.name);

  if (params.slug !== slug) {
    //Redirect to the correct slug
    return {
      redirect: {
        destination: `/collection/${id}/${slug}`,
        statusCode: 301,
      },
    };
  }

  const isInitialLoggedIn = token !== getConfig().publicRuntimeConfig.API_TOKEN;

  return {
    props: {
      isInitialLoggedIn,
      collection,
    },
  };
};

export default CollectionDynamicPage;
